// fibTool.js — shared "Fib Retracement" click-drag multi-drawing tool for both charts
// (Dashboard's SignalChart.js and the More Info chart in App.js). Third of the four
// "advanced drawing" tools. Interaction and persistence are IDENTICAL to
// trendLineTool.js's pattern (2-point click-drag, persisted list, multi-drawing mode) —
// this file mirrors that structure deliberately rather than trying to share it, same
// rationale as trendLineTool.js's own header comment (the two hooks are close enough to
// copy-paste but not close enough to abstract for a 3rd/4th caller without real cost).
//
// v1 simplification: the 7 standard retracement levels (0%, 23.6%, 38.2%, 50%, 61.8%,
// 78.6%, 100%) render spanning only the x1..x2 width of the drag itself, NOT extended to
// the pane's right edge like TradingView does. Same "good enough" tradeoff as
// volumeProfile.js's bucket-midpoint simplification — flagged here for the same reason.

import { useState, useRef, useEffect, useCallback } from "react";
import { fetchAdvancedDrawings, insertAdvancedDrawing, deleteAdvancedDrawing } from "./advancedDrawingsStore";

const TYPE = "fib";
// Classic TradingView Fib Retracement theme: each of the 6 intervals between the 7
// standard levels gets its own translucent FILLED band (not just a colored line) —
// red → orange → green → teal → blue-teal → blue, per the founder's reference
// screenshot. BAND_COLORS[i] is the fill for the interval between LEVELS[i] and
// LEVELS[i+1]; each level's own line/label reuses the color of the band starting at
// that level (100% reuses the last band's color, matching the reference image's
// bottom band). Distinct from Trend Line/Channel's single fixed-color convention on
// purpose — same rationale this codebase already applies per-category for S/R
// (#4ade80/#ea580c) and FVG (#fbbf24).
// Time↔pixel helpers that extrapolate linearly from the visible range when the native
// coordinateToTime/timeToCoordinate return null — confirmed live: chart.timeScale()
// .coordinateToTime(x) returns null once x moves past the last (or before the first)
// actual candle, which made drags into that whitespace margin visibly stop short of the
// cursor/crosshair. Without the same fix on the timeToCoordinate side, a drawing anchored
// out there would also vanish on reload (the projection couldn't resolve it back to a
// pixel). Both directions build the same linear model from the visible range's own
// from/to time and pixel bounds.
function xToTime(chart, x) {
  const ts = chart.timeScale();
  const t = ts.coordinateToTime(x);
  if (t != null) return t;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || xFrom === xTo) return null;
  return range.from + ((x - xFrom) / (xTo - xFrom)) * (range.to - range.from);
}
function timeToX(chart, time) {
  const ts = chart.timeScale();
  const x = ts.timeToCoordinate(time);
  if (x != null) return x;
  const range = ts.getVisibleRange();
  if (!range) return null;
  const xFrom = ts.timeToCoordinate(range.from);
  const xTo = ts.timeToCoordinate(range.to);
  if (xFrom == null || xTo == null || range.to === range.from) return null;
  return xFrom + ((time - range.from) / (range.to - range.from)) * (xTo - xFrom);
}

const BAND_COLORS = ["#f2545b", "#ff9800", "#4caf50", "#089981", "#2696d2", "#2962ff"];
const LEVELS = [
  { ratio: 0, color: BAND_COLORS[0] },
  { ratio: 0.236, color: BAND_COLORS[1] },
  { ratio: 0.382, color: BAND_COLORS[2] },
  { ratio: 0.5, color: BAND_COLORS[3] },
  { ratio: 0.618, color: BAND_COLORS[4] },
  { ratio: 0.786, color: BAND_COLORS[5] },
  { ratio: 1, color: BAND_COLORS[5] },
];

export function useFibTool({ chartRef, seriesRef, containerRef, userId, symbol, context }) {
  const [fibMode, setFibMode] = useState(false);
  const [myFibs, setMyFibs] = useState([]); // [{id, time1, price1, time2, price2}] — DATA space
  const [draft, setDraft] = useState(null); // in-progress drag, DATA space, not yet persisted
  const [, setRangeTick] = useState(0);
  const draggingRef = useRef(false);
  const draftRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setMyFibs([]);
    if (!userId || !symbol || !context) return;
    fetchAdvancedDrawings(userId, symbol, context, TYPE)
      .then((rows) => {
        if (cancelled) return;
        setMyFibs(rows.map((r) => ({ id: r.id, ...r.points })));
      })
      .catch((e) => console.error("[chart_advanced_drawings-read]", e?.message || e));
    return () => { cancelled = true; };
  }, [userId, symbol, context]);

  const pointFromEvent = useCallback((e) => {
    const container = containerRef.current;
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!container || !chart || !series) return null;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const price = series.coordinateToPrice(y);
    const time = xToTime(chart, x);
    if (price == null || time == null) return null;
    return { price, time };
  }, [chartRef, seriesRef, containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!fibMode || !container || !userId) return;

    const handleMouseDown = (e) => {
      const p = pointFromEvent(e);
      if (!p) return;
      draggingRef.current = true;
      chartRef.current?.applyOptions({ handleScroll: false, handleScale: false });
      const next = { time1: p.time, price1: p.price, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseMove = (e) => {
      if (!draggingRef.current) return;
      const p = pointFromEvent(e);
      if (!p) return;
      const prev = draftRef.current;
      if (!prev) return;
      const next = { ...prev, time2: p.time, price2: p.price };
      draftRef.current = next;
      setDraft(next);
    };
    const handleMouseUp = () => {
      const wasDragging = draggingRef.current;
      draggingRef.current = false;
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
      if (!wasDragging) return;
      const finished = draftRef.current;
      draftRef.current = null;
      setDraft(null);
      if (!finished || (finished.time1 === finished.time2 && finished.price1 === finished.price2)) return;
      insertAdvancedDrawing(userId, symbol, context, TYPE, finished)
        .then((row) => setMyFibs((prev) => [...prev, { id: row.id, ...row.points }]))
        .catch((e) => console.error("[chart_advanced_drawings-insert]", e?.message || e));
      // Deliberately does NOT disarm — multi-drawing mode, same as Trend Line/Draw Line.
    };

    container.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      chartRef.current?.applyOptions({ handleScroll: true, handleScale: true });
    };
  }, [fibMode, containerRef, chartRef, pointFromEvent, userId, symbol, context]);

  const deleteFib = useCallback((id) => {
    if (!userId) return;
    deleteAdvancedDrawing(userId, id)
      .then(() => setMyFibs((prev) => prev.filter((f) => f.id !== id)))
      .catch((e) => console.error("[chart_advanced_drawings-delete]", e?.message || e));
  }, [userId]);

  // WATCHDOG version (2026-07-09) — re-attaches when the chart instance appears or is
  // recreated, immediate tick on attach. See trendLineTool.js comment for why.
  useEffect(() => {
    if (myFibs.length === 0 && !draft) return;
    const handler = () => setRangeTick((t) => t + 1);
    let subscribed = null;
    const ensure = () => {
      const chart = chartRef.current;
      if (chart === subscribed) return;
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
      subscribed = chart || null;
      if (chart) {
        try { chart.timeScale().subscribeVisibleLogicalRangeChange(handler); handler(); } catch (e) { subscribed = null; }
      }
    };
    ensure();
    const watchdog = setInterval(ensure, 400);
    return () => {
      clearInterval(watchdog);
      if (subscribed) { try { subscribed.timeScale().unsubscribeVisibleLogicalRangeChange(handler); } catch (e) { /* torn down */ } }
    };
  }, [chartRef, myFibs.length, draft]);

  // Derived: project each fib's two anchor points + every level's price to pixel space,
  // recomputed every render.
  const projectedFibs = [];
  const chart = chartRef.current;
  const series = seriesRef.current;
  if (chart && series) {
    const project = (pts, id, isDraft) => {
      const x1 = timeToX(chart, pts.time1);
      const x2 = timeToX(chart, pts.time2);
      const y1 = series.priceToCoordinate(pts.price1);
      const y2 = series.priceToCoordinate(pts.price2);
      if (x1 == null || x2 == null || y1 == null || y2 == null) return null;
      const rawLevels = LEVELS.map(({ ratio, color }) => {
        const price = pts.price1 + (pts.price2 - pts.price1) * ratio;
        const y = series.priceToCoordinate(price);
        return { ratio, color, price, y };
      });
      // Bands: one filled rectangle per interval between consecutive levels, using each
      // band's own color (BAND_COLORS[i]), skipped only if either boundary failed to
      // project (edge of visible range).
      const bands = [];
      for (let i = 0; i < rawLevels.length - 1; i++) {
        const a = rawLevels[i];
        const b = rawLevels[i + 1];
        if (a.y == null || b.y == null) continue;
        bands.push({ y0: a.y, y1: b.y, color: BAND_COLORS[i] });
      }
      const levels = rawLevels.filter((l) => l.y != null);
      return { id, x1, y1, x2, y2, levels, bands, isDraft };
    };
    myFibs.forEach((f) => {
      const p = project(f, f.id, false);
      if (p) projectedFibs.push(p);
    });
    if (draft) {
      const p = project(draft, null, true);
      if (p) projectedFibs.push(p);
    }
  }

  return { fibMode, setFibMode, myFibs, deleteFib, projectedFibs };
}

// SVG overlay — classic TradingView Fib Retracement look: a translucent filled band per
// interval between consecutive levels (the actual "theme" being matched — see this
// file's header comment), each level's own line + price/percent label in that band's
// color, and a dashed grey anchor line for the original two drag points. Bands/lines
// span the same x1..x2 width as the drag (v1 simplification — see header comment).
// pointerEvents: "none" on the SVG root — see measureTool.js's MeasureOverlay comment
// for why that's critical (without it, the overlay eats every mouse event on the chart).
export function FibOverlay({ fibs }) {
  if (!fibs || fibs.length === 0) return null;
  return (
    <svg
      style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 5, pointerEvents: "none" }}
    >
      {fibs.map((f, i) => {
        const minX = Math.min(f.x1, f.x2);
        const maxX = Math.max(f.x1, f.x2);
        return (
          <g key={f.id ?? `draft-${i}`} opacity={f.isDraft ? 0.5 : 1}>
            {f.bands.map((b, bi) => (
              <rect
                key={bi}
                x={minX} y={Math.min(b.y0, b.y1)}
                width={maxX - minX} height={Math.max(1, Math.abs(b.y1 - b.y0))}
                fill={b.color} fillOpacity="0.08"
              />
            ))}
            <line x1={f.x1} y1={f.y1} x2={f.x2} y2={f.y2} stroke="#9598a1" strokeWidth="1" strokeDasharray="4 3" />
            {f.levels.map((l) => (
              <g key={l.ratio}>
                <line x1={minX} y1={l.y} x2={maxX} y2={l.y} stroke={l.color} strokeWidth="1" />
                <text x={maxX + 4} y={l.y + 3} fill={l.color} fontSize="10" fontFamily="system-ui">
                  {(l.ratio * 100).toFixed(1)}% ({l.price.toLocaleString(undefined, { maximumFractionDigits: 2 })})
                </text>
              </g>
            ))}
          </g>
        );
      })}
    </svg>
  );
}
